// adapter.ts — the single normalization point from API response envelopes to
// the graph's {nodes, edges} shape. Nothing outside this module should touch
// asset bodies, relations blocks or children pages. When a dedicated backend
// /graph/neighbors endpoint lands (phase 2), only this module changes.
//
// All functions here are pure: they take response fragments in and return
// GraphNode[] / GraphEdge[] out. Deduplication by uid is the caller's job via
// mergeGraph(), which folds new fragments into an existing node/edge map.

import { mdiPlus } from '@mdi/js'

import type { components } from '../../api/schema'
import type { IconTileGradient } from '../ui/icon-tile'
import { iconPathFor } from '../../lib/asset-type-icons'
import {
    ASSET_FAMILY_FALLBACK_ICON,
    assetTypeGradient,
    type AssetTypeLike,
} from '../assets/AssetTypeTile'

type Asset = components['schemas']['Asset']
type AssetRelation = components['schemas']['AssetRelation']
type AssetRelationRef = components['schemas']['AssetRelationRef']
type AssetRelationsBlock = components['schemas']['AssetRelationsBlock']

// The inline property shape embedded on an Asset body when the caller passes
// `include=properties` on GET /assets (list endpoint only — the single-asset
// GET does not support this include). Mirrors backend
// internal/models.AssetInlineProperty: {uid, code, value, source}.
interface AssetInlineProperty {
    code: string
    value?: unknown
}

// A single property value rendered on a node (max ~3 shown).
export interface GraphNodeProperty {
    code: string
    value: string
}

// The non-asset entity kinds the explorer can pull in on demand (§4). Assets
// are the hierarchy; these hang laterally off an asset as leaves (except
// interventions, which can further expand to their expenses).
export type EntityKind = 'document' | 'intervention' | 'expense' | 'contact' | 'contract'

export interface GraphNode {
    // Stable id used by React Flow. Equal to the asset uid for real assets, a
    // synthetic id (`more:<parentUid>` / `more:<kind>:<uid>`) for "+N more"
    // pseudo-nodes, or `<kind>:<uid>` for non-asset entity nodes.
    id: string
    // The asset uid (undefined for pseudo-nodes and non-asset entity nodes).
    assetUid?: string
    // The raw entity uid used for detail-page navigation (non-asset kinds).
    entityUid?: string
    name: string
    // What the node's tile draws (spec 2026-09-17-04). Absent on the kinds
    // that draw a section icon of their own (non-asset entities).
    icon?: GraphNodeIcon
    assetTypeCode?: string
    assetTypeUid?: string
    properties: GraphNodeProperty[]
    // Document uid of the asset picture, if known.
    pictureDocumentUid?: string
    // Node kind: a real asset, a non-asset entity, or a "+N more" pseudo-node.
    kind: 'asset' | 'more' | EntityKind
    // "+N more" pseudo-node metadata (pagination overflow).
    moreParentUid?: string
    moreRemaining?: number
    moreNextOffset?: number
    // When set, the "+N more" node loads the next page of this non-asset kind
    // (rather than a children page).
    moreKind?: EntityKind
    // Intervention nodes carry their uid here too so an expense expanded off the
    // same asset can be edged back to its intervention when both are present.
    interventionUid?: string
    // Per-kind count of connected entities (from the asset's link_counts, when
    // fetched with include=link_counts). Drives the per-kind count badges (§6);
    // a kind with 0 or no entry renders no badge.
    linkCounts?: Partial<Record<EntityKind, number>>
    // Count of typed asset-to-asset relations (link_counts.relations) — not an
    // EntityKind, so it gets its own non-toggle badge next to the kind
    // toggles (spec 2026-08-16-03 §6).
    relationsCount?: number
}

export type GraphEdgeCategory = 'hierarchy' | 'relation'

export interface GraphEdge {
    id: string
    source: string
    target: string
    category: GraphEdgeCategory
    // Only set for typed relations (rendered as the edge label).
    relationType?: string
    // Localized relation type name, when available from relation_type_details.
    label?: string
}

export interface Graph {
    nodes: GraphNode[]
    edges: GraphEdge[]
}

/**
 * What a graph node draws in its tile. Either a user's own emoji for THIS
 * asset (kept, on a neutral tile) or an MDI path — the type's `icon` through
 * the registry, else its family's default glyph.
 */
export interface GraphNodeIcon {
    emoji?: string
    path?: string
    gradient: IconTileGradient
}

/**
 * resolveIcon replaces the old resolveEmoji (spec 2026-09-17-04): a per-asset
 * emoji still wins, everything else becomes a glyph coloured by family, and
 * the 📁 fallback is gone — an unknown type lands on `neutral`/`shape-outline`
 * through assetTypeGradient's own last rule.
 */
function resolveIcon(
    perAssetEmoji: string | null | undefined,
    assetTypeCode: string | undefined,
    assetTypeByCode?: (code: string | undefined) => AssetTypeLike | undefined,
): GraphNodeIcon {
    const type =
        assetTypeByCode?.(assetTypeCode) ?? (assetTypeCode ? { code: assetTypeCode } : undefined)
    const gradient = assetTypeGradient(type)
    if (perAssetEmoji) return { emoji: perAssetEmoji, gradient: 'neutral' }
    return {
        path: iconPathFor(type?.icon) ?? ASSET_FAMILY_FALLBACK_ICON[gradient],
        gradient,
    }
}

/** The "+N more" pseudo-node's glyph: a plus on a neutral tile. */
const MORE_NODE_ICON: GraphNodeIcon = { path: mdiPlus, gradient: 'neutral' }

/**
 * The glyph of an asset the graph knows only by uid — a parent or a linked
 * asset whose body has not been fetched. Neutral, because nothing about its
 * type is known yet; it is replaced the moment the body lands.
 */
export const PLACEHOLDER_ASSET_ICON: GraphNodeIcon = {
    path: ASSET_FAMILY_FALLBACK_ICON.neutral,
    gradient: 'neutral',
}

// decodeIfRawJSONB works around a known quirk of
// GET /assets/{uid}/relations?include_properties=…: unlike the assets-list
// `include=properties` path (internal/handlers/assets/service.go,
// loadInlinePropertiesForAssets), the relations enrichment
// (relation_service.go enrichRelationRefs) does not decode the Postgres jsonb
// column before scanning it into `any` — Go's encoding/json then base64-encodes
// the raw bytes, so a plain jsonb scalar like `123` or `"Otis"` can arrive here
// as a base64 string (e.g. "MTIz") instead of the real value. This is a
// narrow, low-risk decode done at the normalization boundary: only strings
// that look like base64 AND decode to valid JSON are unwrapped; anything else
// — including a genuine text property value — passes through untouched.
function decodeIfRawJSONB(raw: unknown): unknown {
    if (typeof raw !== 'string' || raw.length === 0 || raw.length % 4 !== 0) return raw
    if (!/^[A-Za-z0-9+/]+={0,2}$/.test(raw)) return raw
    try {
        return JSON.parse(atob(raw))
    } catch {
        return raw
    }
}

// pickProperties turns a ref's `properties` bag (a {code: value} map, as
// embedded by GET /assets/{uid}/relations?include_properties=…) into up to 3
// display rows.
function pickProperties(
    props: { [key: string]: unknown } | undefined,
    max = 3,
): GraphNodeProperty[] {
    if (!props) return []
    const out: GraphNodeProperty[] = []
    for (const [code, rawIn] of Object.entries(props)) {
        if (rawIn == null) continue
        const raw = decodeIfRawJSONB(rawIn)
        if (raw == null) continue
        let value: string
        if (typeof raw === 'object') {
            // Property values can be wrapped objects ({value: ...}) or scalars.
            const inner = (raw as { value?: unknown }).value
            value = inner == null ? JSON.stringify(raw) : String(inner)
        } else {
            value = String(raw)
        }
        out.push({ code, value })
        if (out.length >= max) break
    }
    return out
}

// pickInlineProperties turns an asset body's `properties` array (embedded by
// GET /assets?include=properties on the list endpoint — every currently-set
// value, not filtered by code) into up to 3 display rows. Skips null/empty
// values and stringifies objects defensively.
function pickInlineProperties(
    items: AssetInlineProperty[] | undefined,
    max = 3,
): GraphNodeProperty[] {
    if (!items) return []
    const out: GraphNodeProperty[] = []
    for (const item of items) {
        if (!item?.code || item.value == null || item.value === '') continue
        const value =
            typeof item.value === 'object' ? JSON.stringify(item.value) : String(item.value)
        out.push({ code: item.code, value })
        if (out.length >= max) break
    }
    return out
}

interface AdapterCtx {
    // Resolve the asset type behind a code (from the loaded types list) — its
    // `icon` and the fields assetTypeGradient reads for the family colour.
    assetTypeByCode?: (code: string | undefined) => AssetTypeLike | undefined
    // Resolve {code, emoji} from an asset_type_uid — the Asset body carries only
    // the uid (no code), so this recovers the type code for color/icon.
    assetTypeMetaByUid?: (uid: string | null | undefined) => { code?: string; emoji?: string } | undefined
}

// nodeFromAsset builds a node from a full asset body. Property rows come from
// one of two sources (see graph.tsx for which is fetched when):
//   - `ownProperties`, when the caller already resolved them via a dedicated
//     GET /assets/{uid}/properties call (used for the focal/expanded asset,
//     whose own GET does not support `include=properties`);
//   - otherwise the asset body's own `.properties` array, present when the
//     caller fetched it via `include=properties` (children pages only — the
//     assets *list* endpoint supports this include, the single-asset GET does
//     not).
export function nodeFromAsset(
    asset: Asset,
    ctx: AdapterCtx = {},
    ownProperties?: GraphNodeProperty[],
): GraphNode {
    const meta = ctx.assetTypeMetaByUid?.(asset.asset_type_uid)
    const assetTypeCode = assetTypeCodeOf(asset) ?? meta?.code
    return {
        id: asset.uid,
        assetUid: asset.uid,
        name: asset.name || asset.uid,
        icon: resolveIcon(asset.emoji, assetTypeCode, ctx.assetTypeByCode),
        assetTypeCode,
        assetTypeUid: asset.asset_type_uid ?? undefined,
        properties:
            ownProperties ??
            pickInlineProperties((asset as unknown as { properties?: AssetInlineProperty[] }).properties),
        pictureDocumentUid: pictureUidOf(asset),
        kind: 'asset',
        linkCounts: mapLinkCounts(asset.link_counts),
        relationsCount: asset.link_counts?.relations,
    }
}

// mapLinkCounts converts the backend AssetLinkCounts (documents/contracts/…) to
// the per-kind map keyed by EntityKind used by the node badges.
function mapLinkCounts(
    lc: components['schemas']['AssetLinkCounts'] | undefined,
): Partial<Record<EntityKind, number>> | undefined {
    if (!lc) return undefined
    return {
        document: lc.documents ?? 0,
        contract: lc.contracts ?? 0,
        expense: lc.expenses ?? 0,
        intervention: lc.interventions ?? 0,
        contact: lc.contacts ?? 0,
    }
}

// nodeFromRef builds a lightweight node from a relation ref (neighbor we have
// not fully fetched yet).
export function nodeFromRef(ref: AssetRelationRef, ctx: AdapterCtx = {}): GraphNode {
    return {
        id: ref.uid,
        assetUid: ref.uid,
        name: ref.name || ref.uid,
        icon: resolveIcon(undefined, ref.asset_type_code, ctx.assetTypeByCode),
        assetTypeCode: ref.asset_type_code,
        assetTypeUid: ref.asset_type_uid ?? undefined,
        properties: pickProperties(ref.properties),
        kind: 'asset',
    }
}

// assetTypeCodeOf resolves an asset's type code. The Asset body carries only
// asset_type_uid; some responses embed the code in frontend_data, so we probe
// a couple of likely spots and fall back to undefined (color/emoji still
// derive gracefully).
function assetTypeCodeOf(asset: Asset): string | undefined {
    const anyAsset = asset as unknown as {
        asset_type_code?: string
        frontend_data?: { asset_type_code?: string; asset_type?: { code?: string } }
    }
    return (
        anyAsset.asset_type_code ??
        anyAsset.frontend_data?.asset_type_code ??
        anyAsset.frontend_data?.asset_type?.code
    )
}

// pictureUidOf extracts the picture document uid from an asset body fetched
// with include=picture (shape tolerated defensively across envelope variants).
function pictureUidOf(asset: Asset): string | undefined {
    const anyAsset = asset as unknown as {
        picture?: { document_uid?: string; uid?: string } | string
        picture_document_uid?: string
    }
    if (typeof anyAsset.picture === 'string') return anyAsset.picture
    return (
        anyAsset.picture?.document_uid ??
        anyAsset.picture?.uid ??
        anyAsset.picture_document_uid
    )
}

// hierarchyEdge builds a plain parent→child edge (parent is source).
function hierarchyEdge(parentUid: string, childUid: string): GraphEdge {
    return {
        id: `h:${parentUid}->${childUid}`,
        source: parentUid,
        target: childUid,
        category: 'hierarchy',
    }
}

// relationEdge builds a typed, directional relation edge (source→target).
function relationEdge(rel: AssetRelation, locale: string): GraphEdge | null {
    const source = rel.source?.uid
    const target = rel.target?.uid
    if (!source || !target) return null
    const details = rel.relation_type_details
    const label =
        details?.name?.[locale] ?? details?.name?.en ?? rel.relation_type
    return {
        id: `r:${rel.uid}`,
        source,
        target,
        category: 'relation',
        relationType: rel.relation_type,
        label,
    }
}

// expandAsset produces every node and edge discoverable one hop from a focal
// asset body: the focal node itself (enriched), its typed relation neighbors
// (incoming + outgoing) with labelled edges, and its parent (if any) with a
// hierarchy edge. Children come from expandChildren (separate endpoint).
export function expandAsset(
    asset: Asset,
    opts: {
        locale: string
        ctx?: AdapterCtx
        // Overrides asset.relations. Pass the response of
        // GET /assets/{uid}/relations?include_properties=<codes> so neighbor
        // refs carry property values (the plain asset GET embeds relations
        // unconditionally, but never with include_properties — see graph.tsx).
        relationsBlock?: AssetRelationsBlock
        // Property rows for the asset's OWN node (see nodeFromAsset).
        ownProperties?: GraphNodeProperty[]
    },
): Graph {
    const { locale, ctx = {}, ownProperties } = opts
    const nodes: GraphNode[] = [nodeFromAsset(asset, ctx, ownProperties)]
    const edges: GraphEdge[] = []

    const block = opts.relationsBlock ?? asset.relations
    if (block) {
        for (const rel of block.outgoing ?? []) {
            // group_member renders like any other typed relation (spec §node/edge).
            if (rel.target) nodes.push(nodeFromRef(rel.target, ctx))
            const edge = relationEdge(rel, locale)
            if (edge) edges.push(edge)
        }
        for (const rel of block.incoming ?? []) {
            if (rel.source) nodes.push(nodeFromRef(rel.source, ctx))
            const edge = relationEdge(rel, locale)
            if (edge) edges.push(edge)
        }
    }

    // Parent (hierarchy). We only know its uid here; a placeholder node is
    // added and enriched later if/when it is itself expanded.
    const parentUid = asset.parent_asset_uid
    if (parentUid) {
        nodes.push({
            id: parentUid,
            assetUid: parentUid,
            name: parentUid,
            icon: resolveIcon(undefined, undefined, ctx.assetTypeByCode),
            properties: [],
            kind: 'asset',
        })
        edges.push(hierarchyEdge(parentUid, asset.uid))
    }

    return { nodes, edges }
}

// A page of children as returned by GET /assets?parent_asset_uid=X&limit=N.
export interface ChildrenPage {
    parentUid: string
    items: Asset[]
    total: number
    limit: number
    offset: number
}

// expandChildren produces child nodes + hierarchy edges from a children page,
// plus a "+N more" pseudo-node when the page did not exhaust the children.
export function expandChildren(page: ChildrenPage, ctx: AdapterCtx = {}): Graph {
    const nodes: GraphNode[] = []
    const edges: GraphEdge[] = []
    for (const child of page.items) {
        nodes.push(nodeFromAsset(child, ctx))
        edges.push(hierarchyEdge(page.parentUid, child.uid))
    }
    const seen = page.offset + page.items.length
    const remaining = page.total - seen
    if (remaining > 0) {
        const moreId = `more:${page.parentUid}`
        nodes.push({
            id: moreId,
            name: '',
            icon: MORE_NODE_ICON,
            properties: [],
            kind: 'more',
            moreParentUid: page.parentUid,
            moreRemaining: remaining,
            moreNextOffset: seen,
        })
        edges.push({
            id: `hmore:${page.parentUid}`,
            source: page.parentUid,
            target: moreId,
            category: 'hierarchy',
        })
    }
    return { nodes, edges }
}

// A normalized reference to a non-asset entity, produced by graph.tsx from each
// kind's list-endpoint response (documents/interventions/expenses/contacts all
// have different field names, so the route flattens them to {uid, name}).
export interface EntityRef {
    uid: string
    name: string
    // Expense→intervention link, so the edge can be drawn when both are present.
    interventionUid?: string
}

// entityNode builds a leaf node for a non-asset kind. Its id is namespaced by
// kind so a document and an asset that happen to share a uid never collide.
export function entityNode(kind: EntityKind, ref: EntityRef): GraphNode {
    return {
        id: `${kind}:${ref.uid}`,
        entityUid: ref.uid,
        name: ref.name || ref.uid,
        properties: [],
        kind,
        interventionUid: kind === 'intervention' ? ref.uid : ref.interventionUid,
    }
}

// A page of one non-asset kind fetched for an asset (or, for expenses, an
// intervention). Mirrors ChildrenPage but for entity refs.
export interface EntityPage {
    // The node these entities hang off (an asset uid, or `intervention:<uid>`).
    anchorId: string
    kind: EntityKind
    items: EntityRef[]
    total: number
    limit: number
    offset: number
}

// expandEntities turns a page of non-asset entities into leaf nodes edged off
// their anchor, plus a kind-scoped "+N more" pseudo-node on overflow.
export function expandEntities(page: EntityPage): Graph {
    const nodes: GraphNode[] = []
    const edges: GraphEdge[] = []
    for (const ref of page.items) {
        const n = entityNode(page.kind, ref)
        nodes.push(n)
        edges.push({
            id: `e:${page.anchorId}->${n.id}`,
            source: page.anchorId,
            target: n.id,
            category: 'relation',
        })
    }
    const seen = page.offset + page.items.length
    const remaining = page.total - seen
    if (remaining > 0) {
        const moreId = `more:${page.kind}:${page.anchorId}`
        nodes.push({
            id: moreId,
            name: '',
            icon: MORE_NODE_ICON,
            properties: [],
            kind: 'more',
            moreParentUid: page.anchorId,
            moreKind: page.kind,
            moreRemaining: remaining,
            moreNextOffset: seen,
        })
        edges.push({
            id: `emore:${page.kind}:${page.anchorId}`,
            source: page.anchorId,
            target: moreId,
            category: 'relation',
        })
    }
    return { nodes, edges }
}

// mergeGraph folds `incoming` into `base`, deduplicating nodes and edges by id.
// A real (kind:'asset') node with a resolved name/type wins over a bare
// placeholder that carries only its uid as the name — so a parent placeholder
// gets upgraded once the real body arrives.
export function mergeGraph(base: Graph, incoming: Graph): Graph {
    const nodeMap = new Map<string, GraphNode>()
    for (const n of base.nodes) nodeMap.set(n.id, n)
    for (const n of incoming.nodes) {
        const existing = nodeMap.get(n.id)
        if (!existing) {
            nodeMap.set(n.id, n)
            continue
        }
        nodeMap.set(n.id, preferRicher(existing, n))
    }

    const edgeMap = new Map<string, GraphEdge>()
    for (const e of base.edges) edgeMap.set(e.id, e)
    for (const e of incoming.edges) if (!edgeMap.has(e.id)) edgeMap.set(e.id, e)

    return { nodes: [...nodeMap.values()], edges: [...edgeMap.values()] }
}

// preferRicher keeps whichever version of a node carries more real data. A
// placeholder is detected by name === uid and no asset type.
function isPlaceholder(n: GraphNode): boolean {
    return n.name === n.id && !n.assetTypeCode && !n.assetTypeUid
}

function preferRicher(a: GraphNode, b: GraphNode): GraphNode {
    if (isPlaceholder(a) && !isPlaceholder(b)) return b
    if (!isPlaceholder(a) && isPlaceholder(b)) return a
    // Merge properties/picture opportunistically, keeping the first non-empty.
    return {
        ...a,
        ...b,
        properties: b.properties.length ? b.properties : a.properties,
        pictureDocumentUid: a.pictureDocumentUid ?? b.pictureDocumentUid,
    }
}
